import React, { useState, useEffect } from 'react';
import {
    Headphones, MessageSquare, Mail, Phone, Clock, Send,
    Copy, Check, ExternalLink, AlertCircle, CheckCircle2,
    UploadCloud, X, Loader2, Sparkles, ShieldCheck
} from 'lucide-react';
import api from '../services/api';

const SuporteScreen = ({ currentUser, companyData }) => {
    const [supportConfig, setSupportConfig] = useState({
        supportWhatsapp: '',
        supportEmail: '',
        supportMessage: 'Olá! Preciso de suporte com a plataforma WaDesk.',
        supportHours: 'Segunda a Sexta, das 08h às 18h'
    });
    const [loadingConfig, setLoadingConfig] = useState(true);

    // Form de chamado
    const [reason, setReason] = useState('');
    const [contactPhone, setContactPhone] = useState(currentUser?.phone || companyData?.phone || '');
    const [images, setImages] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submittedTicketId, setSubmittedTicketId] = useState(null);

    // Feedback de cópia
    const [copiedWhatsapp, setCopiedWhatsapp] = useState(false);
    const [copiedEmail, setCopiedEmail] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/system/settings');
                if (res.data) {
                    setSupportConfig({
                        supportWhatsapp: res.data.supportWhatsapp || '',
                        supportEmail: res.data.supportEmail || '',
                        supportMessage: res.data.supportMessage || 'Olá! Preciso de suporte com a plataforma WaDesk.',
                        supportHours: res.data.supportHours || 'Segunda a Sexta, das 08h às 18h'
                    });
                }
            } catch (e) {
                console.error("Erro ao carregar canais de suporte:", e);
            } finally {
                setLoadingConfig(false);
            }
        };
        fetchSettings();
    }, []);

    // Limpa o número para gerar o link do WhatsApp
    const rawPhone = supportConfig.supportWhatsapp ? supportConfig.supportWhatsapp.replace(/\D/g, '') : '';
    const formattedPhone = supportConfig.supportWhatsapp || 'Não configurado';

    // Monta a mensagem personalizada para o WhatsApp
    const companyName = companyData?.name || currentUser?.companyName || 'Minha Empresa';
    const userName = currentUser?.name || 'Cliente';
    const fullWaMessage = `${supportConfig.supportMessage}\n\n*Empresa:* ${companyName}\n*Solicitante:* ${userName}`;
    const waUrl = rawPhone ? `https://wa.me/${rawPhone}?text=${encodeURIComponent(fullWaMessage)}` : null;

    const mailtoUrl = supportConfig.supportEmail
        ? `mailto:${supportConfig.supportEmail}?subject=${encodeURIComponent(`Suporte WaDesk - ${companyName}`)}&body=${encodeURIComponent(`Olá equipe de suporte,\n\nSolicitante: ${userName}\nEmpresa: ${companyName}\n\nDescreva sua dúvida ou problema aqui:\n`)}`
        : null;

    const handleCopy = (text, type) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        if (type === 'whatsapp') {
            setCopiedWhatsapp(true);
            setTimeout(() => setCopiedWhatsapp(false), 2000);
        } else if (type === 'email') {
            setCopiedEmail(true);
            setTimeout(() => setCopiedEmail(false), 2000);
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (images.length + files.length > 5) {
            return alert("Você pode anexar no máximo 5 imagens.");
        }
        setImages(prev => [...prev, ...files]);
    };

    const handleRemoveImage = (index) => {
        setImages(images.filter((_, i) => i !== index));
    };

    const handleSubmitTicket = async (e) => {
        e.preventDefault();
        if (!reason.trim()) return alert("Por favor, descreva o motivo do seu contato.");

        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('cpf_cnpj', companyData?.cpf_cnpj || currentUser?.cpf_cnpj || 'Não informado');
            formData.append('phone', contactPhone || 'Não informado');
            formData.append('reason', `[Empresa: ${companyName}] [Usuário: ${userName}]\n\n${reason}`);

            images.forEach(img => {
                formData.append('images', img);
            });

            const res = await api.post('/public/support', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setSubmittedTicketId(res.data?.ticketId || 'OK');
            setReason('');
            setImages([]);
        } catch (e) {
            alert("Erro ao enviar chamado de suporte: " + (e.response?.data?.error || e.message));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-8 animate-in fade-in duration-300">
            {/* Header com Banner */}
            <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white rounded-2xl p-6 md:p-8 shadow-xl relative overflow-hidden">
                <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
                <div className="relative z-10 max-w-2xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-semibold mb-4 border border-blue-400/30">
                        <Sparkles className="w-3.5 h-3.5" />
                        Canal Direto com Especialistas
                    </div>
                    <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2 flex items-center gap-3">
                        <Headphones className="w-8 h-8 text-blue-400" />
                        Central de Suporte & Atendimento
                    </h1>
                    <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                        Precisa de auxílio técnico, esclarecer dúvidas ou relatar qualquer inconsistência? Escolha o canal de sua preferência abaixo para atendimento imediato.
                    </p>
                </div>
            </div>

            {/* 1. Formulário Principal: Abrir Solicitação de Suporte pelo Sistema */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
                <div className="border-b border-gray-100 pb-4 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Send className="w-5 h-5 text-blue-600" />
                        Abrir Solicitação de Suporte pelo Sistema
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Preencha os detalhes abaixo para registrar seu chamado diretamente com a nossa equipe de suporte.
                    </p>
                </div>

                {submittedTicketId ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center space-y-3">
                        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
                        <h3 className="text-lg font-bold text-emerald-900">Chamado Registrado com Sucesso!</h3>
                        <p className="text-sm text-emerald-700 max-w-lg mx-auto">
                            Sua solicitação foi enviada diretamente aos administradores da plataforma. Entraremos em contato pelo telefone ou e-mail cadastrado o mais breve possível.
                        </p>
                        <button
                            onClick={() => setSubmittedTicketId(null)}
                            className="mt-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg text-sm transition-colors"
                        >
                            Abrir Novo Chamado
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmitTicket} className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Empresa</label>
                                <input
                                    type="text"
                                    disabled
                                    className="w-full p-2.5 bg-gray-100 border rounded-lg text-sm text-gray-600 font-medium"
                                    value={companyName}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Telefone / WhatsApp para Retorno</label>
                                <input
                                    type="text"
                                    className="w-full p-2.5 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                                    placeholder="(21) 99999-9999"
                                    value={contactPhone}
                                    onChange={e => setContactPhone(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Descrição do Problema ou Dúvida</label>
                            <textarea
                                className="w-full p-3 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none h-28"
                                placeholder="Descreva detalhadamente o que está acontecendo ou como podemos te ajudar..."
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                                Anexar Imagens / Prints do Erro (Opcional - até 5 imagens)
                            </label>
                            <div className="flex items-center gap-4">
                                <label className="cursor-pointer border-2 border-dashed border-gray-300 hover:border-blue-500 rounded-xl p-4 flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors w-full md:w-auto">
                                    <UploadCloud className="w-5 h-5" />
                                    <span>Selecionar Imagens</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        onChange={handleImageChange}
                                    />
                                </label>
                                <span className="text-xs text-gray-400">
                                    {images.length > 0 ? `${images.length} arquivo(s) selecionado(s)` : 'PNG, JPG ou JPEG'}
                                </span>
                            </div>

                            {images.length > 0 && (
                                <div className="flex gap-3 mt-3 flex-wrap">
                                    {images.map((img, i) => (
                                        <div key={i} className="relative group border rounded-lg p-1 bg-gray-50 flex items-center gap-2 text-xs">
                                            <span className="truncate max-w-[150px]">{img.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(i)}
                                                className="text-red-500 hover:text-red-700 p-0.5"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="pt-2 flex justify-end">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-xl shadow-md transition-all flex items-center gap-2 text-sm disabled:opacity-50"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Enviando Chamado...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Registrar Chamado
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* 2. Grid de Canais Diretos: WhatsApp e E-mail (Abaixo da Solicitação) */}
            <div>
                <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-indigo-600" />
                        Outros Canais de Atendimento
                    </h3>
                    <p className="text-xs text-gray-500">
                        Se preferir um contato direto ou formal, utilize os canais abaixo:
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* CARD WHATSAPP */}
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-6 flex flex-col justify-between relative overflow-hidden group">
                        <div className="absolute top-0 left-0 right-0 h-1.5 bg-emerald-500"></div>

                        <div>
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center shadow-inner">
                                    <MessageSquare className="w-6 h-6" />
                                </div>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                    Atendimento Rápido
                                </span>
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 mb-1">WhatsApp Oficial</h3>
                            <p className="text-xs text-gray-500 mb-4">
                                Converse em tempo real com a nossa equipe de suporte pelo WhatsApp.
                            </p>

                            <div className="bg-gray-50 border rounded-xl p-3.5 mb-4 flex items-center justify-between">
                                <div>
                                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block">Número</span>
                                    <span className="text-base font-bold text-gray-800 font-mono">
                                        {formattedPhone}
                                    </span>
                                </div>
                                {rawPhone && (
                                    <button
                                        onClick={() => handleCopy(formattedPhone, 'whatsapp')}
                                        className="p-2 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
                                        title="Copiar número"
                                    >
                                        {copiedWhatsapp ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span>{supportConfig.supportHours || 'Segunda a Sexta, das 08h às 18h'}</span>
                            </div>
                        </div>

                        <div>
                            {waUrl ? (
                                <a
                                    href={waUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-4 rounded-xl shadow-md shadow-emerald-600/20 hover:shadow-emerald-600/30 transition-all flex items-center justify-center gap-2 text-sm"
                                >
                                    <MessageSquare className="w-4 h-4" />
                                    Abrir Conversa no WhatsApp
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            ) : (
                                <button
                                    disabled
                                    className="w-full bg-gray-100 text-gray-400 font-bold py-3 px-4 rounded-xl cursor-not-allowed text-sm"
                                >
                                    WhatsApp não configurado
                                </button>
                            )}
                        </div>
                    </div>

                    {/* CARD E-MAIL */}
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-6 flex flex-col justify-between relative overflow-hidden group">
                        <div className="absolute top-0 left-0 right-0 h-1.5 bg-blue-600"></div>

                        <div>
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center shadow-inner">
                                    <Mail className="w-6 h-6" />
                                </div>
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-bold">
                                    <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                                    Suporte Formal
                                </span>
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 mb-1">E-mail de Atendimento</h3>
                            <p className="text-xs text-gray-500 mb-4">
                                Ideal para envio de solicitações formais, relatórios ou dúvidas contratuais.
                            </p>

                            <div className="bg-gray-50 border rounded-xl p-3.5 mb-4 flex items-center justify-between">
                                <div className="truncate mr-2">
                                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block">E-mail</span>
                                    <span className="text-base font-bold text-gray-800 font-mono truncate block">
                                        {supportConfig.supportEmail || 'Não configurado'}
                                    </span>
                                </div>
                                {supportConfig.supportEmail && (
                                    <button
                                        onClick={() => handleCopy(supportConfig.supportEmail, 'email')}
                                        className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                                        title="Copiar e-mail"
                                    >
                                        {copiedEmail ? <Check className="w-4 h-4 text-blue-600" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span>Resposta em até 24 horas úteis</span>
                            </div>
                        </div>

                        <div>
                            {mailtoUrl ? (
                                <a
                                    href={mailtoUrl}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl shadow-md shadow-blue-600/20 hover:shadow-blue-600/30 transition-all flex items-center justify-center gap-2 text-sm"
                                >
                                    <Mail className="w-4 h-4" />
                                    Enviar E-mail de Suporte
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            ) : (
                                <button
                                    disabled
                                    className="w-full bg-gray-100 text-gray-400 font-bold py-3 px-4 rounded-xl cursor-not-allowed text-sm"
                                >
                                    E-mail não configurado
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuporteScreen;
